/**
 * DecisionsPage — project-scoped authoritative decisions.
 * Renders each row with provenance badge, supersession chain link, and
 * approval-state pill. Reads via supabase-js (PostgREST + RLS).
 */
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, GitBranch, Filter } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { supabase } from "@/lib/supabaseClient";
import { useActiveProject } from "@/context/ActiveProjectContext";

function ApprovalPill({ state }) {
    const tone =
        state === "confirmed" ? "text-emerald-300 bg-emerald-500/10" :
        state === "pending" ? "text-qoryn-cyan bg-qoryn-cyan/10" :
        state === "superseded" ? "text-qoryn-textMuted bg-white/5" :
        state === "rejected" ? "text-red-300 bg-red-500/10" :
        "text-qoryn-textDim bg-white/5";
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase ${tone}`}>
            {state}
        </span>
    );
}

export default function DecisionsPage() {
    const { projects } = useActiveProject();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [projectFilter, setProjectFilter] = useState("all");
    const [includeSuperseded, setIncludeSuperseded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        supabase
            .from("decisions")
            .select("id, project_id, decision_text, approval_state, superseded_by, superseded_reason, source_type, source_session_id, created_at")
            .order("created_at", { ascending: false })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) setError(error.message);
                else setRows(data ?? []);
            });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!rows) return [];
        return rows
            .filter((r) => projectFilter === "all" || r.project_id === projectFilter)
            .filter((r) => includeSuperseded || (!r.superseded_by && r.approval_state !== "rejected"));
    }, [rows, projectFilter, includeSuperseded]);

    return (
        <div data-testid="decisions-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Decisions</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Explicit calls, sourced from conversations. Newer decisions can supersede older ones.
                </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 h-9 px-3 rounded-full qoryn-panel font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                    <Filter className="h-3 w-3" strokeWidth={1.75} />
                    Filter
                </div>
                <select
                    data-testid="decisions-project-filter"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-9 px-3 rounded-full qoryn-panel bg-transparent font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text qoryn-focus"
                >
                    <option value="all">All projects</option>
                    {projects.filter((p) => p.status !== "archived").map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <label className="inline-flex items-center gap-1.5 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim cursor-pointer">
                    <input
                        data-testid="decisions-include-superseded"
                        type="checkbox"
                        checked={includeSuperseded}
                        onChange={(e) => setIncludeSuperseded(e.target.checked)}
                        className="accent-qoryn-cyan"
                    />
                    Show superseded
                </label>
            </div>
            {rows === null && !error && (
                <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {error && (
                <EmptyState icon={CheckCircle2} title="Couldn't load decisions." body={error} />
            )}
            {rows !== null && !error && filtered.length === 0 && (
                <EmptyState
                    icon={CheckCircle2}
                    title="No decisions yet."
                    body="Decisions land here after a voice session gets summarized and promoted, or when you record one manually."
                />
            )}
            {filtered.length > 0 && (
                <ul data-testid="decisions-list" className="space-y-2">
                    {filtered.map((d) => (
                        <li key={d.id} data-testid={`decision-row-${d.id}`} className="qoryn-panel rounded-2xl px-5 py-4 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                                <p className="font-sans text-[14px] text-qoryn-text leading-relaxed flex-1">
                                    {d.decision_text}
                                </p>
                                <ApprovalPill state={d.approval_state} />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <ProvenanceBadge sourceType={d.source_type} sourceSessionId={d.source_session_id} />
                                {d.superseded_by && (
                                    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono-ui text-[8px] tracking-widest uppercase text-qoryn-textMuted bg-white/5">
                                        <GitBranch className="h-2.5 w-2.5" strokeWidth={2} />
                                        superseded
                                    </span>
                                )}
                                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                    {new Date(d.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
